'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2, ScanLine, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/use-debounce';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { deviceQueries, type Device } from '@/features/devices';

interface ScannerSelectionPanelProps {
  selectedScannerId: number | null;
  onSelectScanner: (id: number, name: string) => void;
  selectedContainerCount: number;
}

export function ScannerSelectionPanel({
  selectedScannerId,
  onSelectScanner,
  selectedContainerCount
}: ScannerSelectionPanelProps) {
  const t = useTranslations('handoverScanning');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const {
    items: scanners,
    isLoading,
    isFetchingNextPage,
    loadMoreRef
  } = useInfiniteScroll<Device>(
    deviceQueries.infiniteList({
      status: ['online'],
      ...(debouncedSearch ? { search: debouncedSearch } : {})
    })
  );

  return (
    <Card className='flex h-full flex-col'>
      <CardHeader className='space-y-3 pb-3'>
        <div className='flex items-center gap-2'>
          <ScanLine className='text-muted-foreground h-5 w-5' />
          <CardTitle className='text-base'>
            {t('panelAssignmentBoard')}
          </CardTitle>
        </div>

        {/* Selection summary */}
        <div className='space-y-0.5'>
          <p className='text-sm font-medium'>
            {t('selectedCount', { count: selectedContainerCount })}
          </p>
          <p className='text-muted-foreground text-xs'>
            {t('assignToScanner')}
          </p>
        </div>

        {/* Search */}
        <div className='relative'>
          <Search className='text-muted-foreground absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2' />
          <Input
            placeholder={t('searchScanners')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className='ps-9'
          />
        </div>
      </CardHeader>

      <CardContent
        className='flex-1 overflow-y-auto pb-4'
        style={{ maxHeight: 'calc(100vh - 350px)' }}
      >
        {isLoading ? (
          <div className='space-y-3 pt-3'>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className='h-24 w-full rounded-lg' />
            ))}
          </div>
        ) : scanners.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-12 text-center'>
            <ScanLine className='text-muted-foreground mb-3 h-10 w-10' />
            <p className='text-muted-foreground text-sm'>
              {t('noScannersFound')}
            </p>
          </div>
        ) : (
          <div className='space-y-3 pt-3'>
            {scanners.map((scanner) => {
              const isSelected = selectedScannerId === scanner.id;
              const isMaintenance = scanner.status === 'maintenance';

              const card = (
                <button
                  key={scanner.id}
                  type='button'
                  disabled={isMaintenance}
                  onClick={() =>
                    !isMaintenance && onSelectScanner(scanner.id, scanner.name)
                  }
                  className={cn(
                    'w-full rounded-lg border p-3 text-start transition-all',
                    'hover:border-primary/50 hover:bg-primary/5',
                    isSelected && 'ring-primary bg-primary/5 ring-2',
                    isMaintenance && 'cursor-not-allowed opacity-50'
                  )}
                >
                  <div className='space-y-1'>
                    {/* Header row: serial + name */}
                    <div className='flex flex-wrap items-center justify-between gap-2'>
                      <div>
                        <p className='text-sm font-semibold'>
                          {scanner.serial_number}
                        </p>
                        <p className='text-muted-foreground text-xs'>
                          {scanner.name}
                        </p>
                      </div>
                      <span className='text-muted-foreground text-xs'>
                        {t('assignedCount', { count: scanner.current_load })}
                      </span>
                    </div>
                  </div>
                </button>
              );

              if (isMaintenance) {
                return (
                  <TooltipProvider key={scanner.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>{card}</TooltipTrigger>
                      <TooltipContent>
                        <p>{t('maintenanceTooltip')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }

              return card;
            })}

            <div ref={loadMoreRef} className='h-px' />
            {isFetchingNextPage && (
              <div className='flex items-center justify-center py-2'>
                <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
